import { useEffect, useRef, useState } from "react";

import * as Sentry from "@sentry/react";
import { Onfido } from "onfido-sdk-ui";
import type { SdkErrorTypes } from "onfido-sdk-ui/types/shared/Error";
import type { SdkParameters } from "onfido-sdk-ui/types/shared/SdkParameters";
import { useHistory } from "react-router";

import { Button, Spinner, Text, useModal } from "@alpacahq/ui/old";

import AmplitudeProvider from "~/AmplitudeProvider";
import { useMutationRequest } from "~/v2/api/hooks";
import type { Account } from "~/v2/api/rest/account";
import {
  createOnfidoApplicant,
  getOnfidoSdkToken,
  type PatchOnfidoParams,
  patchOnfidoSdkToken,
  updateOnfidoApplicant,
} from "~/v2/api/rest/onfido";
import { Path } from "~/v2/path";
import { eventTracker } from "~/v2/utils/eventTracker";

const RESTART_ONFIDO_ERROR_TYPES: readonly SdkErrorTypes[] = [
  "user_consent_denied",
  "workflow_abandoned",
] as const;

const ONFIDO_SETUP_ERROR_MESSAGE =
  "We couldn't start identity verification. Please try again.";

const ONFIDO_THEME: SdkParameters["theme"] = {
  name: "light",
  config: {
    borderRadiusButton: "8px",
    fontWeightBody: 400,
    fontSizeBody: "14px",
    fontSizeSubtitle: "16px",
    fontSizeTitle: "26px",
    colorContentButtonPrimaryText: "#000000",
    colorBackgroundButtonPrimary: "#FFD700",
    colorBackgroundButtonPrimaryHover: "#ffd900ba",
    colorBackgroundButtonPrimaryActive: "#c4a700",
  },
};

const TOKEN_FETCH_MAX_ATTEMPTS = 3;
const TOKEN_FETCH_DELAY_MS = 300;

const fetchSdkTokenWithRetry = async (accountId: string): Promise<string> => {
  for (let attempt = 1; attempt <= TOKEN_FETCH_MAX_ATTEMPTS; attempt++) {
    try {
      const data = await getOnfidoSdkToken(accountId);
      if (data?.onfido_sdk_token) {
        return data.onfido_sdk_token;
      }
    } catch (error) {
      if (attempt === TOKEN_FETCH_MAX_ATTEMPTS) {
        throw error;
      }
    }

    if (attempt < TOKEN_FETCH_MAX_ATTEMPTS) {
      await new Promise((resolve) => setTimeout(resolve, TOKEN_FETCH_DELAY_MS));
    }
  }

  throw new Error("onfido_sdk_token_missing");
};

type OnfidoPageProps = { account: Account; handleCompleted: () => void };

const OnfidoPage = ({ account, handleCompleted }: OnfidoPageProps) => {
  const history = useHistory();
  const accountId = account?.id;
  const onfidoApplicantId = account?.onfido_applicant_id;
  const onfidoDetailsApplicantId = account?.onfido_details?.applicant_id;
  const [isStarted, setIsStarted] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [setupErrorMessage, setSetupErrorMessage] = useState<string | null>(
    null,
  );
  const [activeSdkToken, setActiveSdkToken] = useState<string | null>(null);

  const isStartingRef = useRef(false);
  const onfidoApplicantIdRef = useRef(onfidoApplicantId);

  useEffect(() => {
    onfidoApplicantIdRef.current = onfidoApplicantId;
  }, [onfidoApplicantId]);

  const captureOnfidoException = (
    error: unknown,
    extraTags?: Record<string, string | undefined>,
  ) => {
    Sentry.captureException(error, {
      tags: {
        flow: "onboarding",
        step: "onfido",
        ...extraTags,
      },
    });
  };

  const showSetupError = (error: unknown) => {
    console.error("Onfido exception:", error);
    captureOnfidoException(error);
    setSetupErrorMessage(ONFIDO_SETUP_ERROR_MESSAGE);
    setIsStarted(false);
    setActiveSdkToken(null);
  };

  const handleExitApplication = () => {
    AmplitudeProvider.dispatch(
      "onfido_abandoned",
      JSON.stringify({ onfido_id: onfidoApplicantId }),
    );
    history.push(Path.ROUTE_DASHBOARD);
  };

  const { mutate: patchSDKToken } = useMutationRequest(
    (body: PatchOnfidoParams) => patchOnfidoSdkToken(body),
  );

  const {
    mutate: createApplicant,
    isPending: isCreatingOnfido,
    isSuccess: isOnfidoCreated,
  } = useMutationRequest<Account, string>(
    (accountID) => createOnfidoApplicant(accountID),
    {
      onSuccess: () => {
        void startOnfido();
      },
      onError: showSetupError,
    },
  );

  const {
    mutate: updateApplicant,
    isPending: isUpdatingOnfido,
    isSuccess: isOnfidoUpdated,
  } = useMutationRequest<Account, string>(
    (accountID) => updateOnfidoApplicant(accountID),
    {
      onSuccess: () => {
        void startOnfido();
      },
      onError: showSetupError,
    },
  );

  const hasApplicant = !!onfidoApplicantId || !!onfidoDetailsApplicantId;
  const isApplicantReady = isOnfidoCreated || isOnfidoUpdated;

  const setupApplicant = () => {
    if (!accountId) {
      return;
    }

    const createOrUpdateApplicant = hasApplicant
      ? updateApplicant
      : createApplicant;

    createOrUpdateApplicant(accountId);
  };

  const startOnfido = async () => {
    if (!accountId || isStartingRef.current) {
      return;
    }

    isStartingRef.current = true;
    setIsStarting(true);
    setIsStarted(false);
    setSetupErrorMessage(null);
    setErrorMessage(null);

    try {
      const sdkToken = await fetchSdkTokenWithRetry(accountId);
      setActiveSdkToken(sdkToken);
      setIsStarted(true);
    } catch (error) {
      showSetupError(error);
    } finally {
      isStartingRef.current = false;
      setIsStarting(false);
    }
  };

  const handleRestartOnfido = () => {
    setSetupErrorMessage(null);
    setErrorMessage(null);
    setIsStarted(false);
    setActiveSdkToken(null);

    if (!isApplicantReady) {
      setupApplicant();
      return;
    }

    void startOnfido();
  };

  const userConsentErrorModal = useModal({
    title: "Identity Verification Required",
    content: (
      <div className="flex flex-col gap-4">
        <Text className="font-medium">We respect your privacy choices.</Text>
        <Text>
          However, to proceed with opening your account, we require your consent
          to process personal data and upload the necessary documents.
        </Text>
        <Text>
          This is essential to comply with legal and regulatory requirements.
        </Text>
      </div>
    ),
    controls: (
      <div className="flex w-full justify-between gap-4">
        <Button variant="red" onClick={handleExitApplication}>
          Exit Application
        </Button>
        <Button
          onClick={() => {
            userConsentErrorModal.toggle();
            handleRestartOnfido();
          }}
        >
          Restart Identity Verification
        </Button>
      </div>
    ),
  });

  const errorModal = useModal({
    title: "Error",
    content: (
      <div className="flex flex-col gap-4">
        {errorMessage && <Text className="font-medium">{errorMessage}</Text>}
        <Text>
          An error occurred while verifying your identity. Please contact
          support at{" "}
          <a href="mailto:support@alpaca.markets">support@alpaca.markets</a> if
          the problem persists.
        </Text>
      </div>
    ),
    controls: (
      <div className="flex w-full justify-between gap-4">
        <Button variant="red" onClick={handleExitApplication}>
          Exit Application
        </Button>
        <Button
          onClick={() => {
            errorModal.toggle();
            handleRestartOnfido();
          }}
        >
          Restart Identity Verification
        </Button>
      </div>
    ),
  });

  // Create or Update Onfido Applicant when Account loads
  // biome-ignore lint/correctness/useExhaustiveDependencies: setupApplicant is an unmemoized local function, so listing it would re-run this effect every render and repeatedly create/update the Onfido applicant. Creating the applicant once per accountId is the intent.
  useEffect(() => {
    if (!accountId) {
      return;
    }

    setupApplicant();
  }, [accountId]);

  // When modal is triggered to open, initialize Onfido
  // biome-ignore lint/correctness/useExhaustiveDependencies: this effect mounts the Onfido SDK. captureOnfidoException, handleCompleted, patchSDKToken and the two modal `toggle` methods are all recreated every render, so listing them would tear down and re-mount the SDK on every render. That is the infinite remount that caused this rule to be rolled back previously; the SDK must be initialised only when the token/account/started state changes.
  useEffect(() => {
    if (!accountId || !activeSdkToken || !isStarted) {
      return;
    }

    const onfidoOptions: SdkParameters = {
      token: activeSdkToken,
      steps: ["document", "face", "complete"],
      onComplete: () => {
        eventTracker("Finished Onfido SDK flow", {
          category: "Onboarding",
        });
        AmplitudeProvider.dispatch(
          "onfido_finished",
          JSON.stringify({ onfido_id: onfidoApplicantIdRef.current }),
        );
        patchSDKToken({
          accountID: accountId,
          outcome: "USER_COMPLETED",
          token: activeSdkToken,
        });
        setIsCompleted(true);
        handleCompleted();
      },
      onError: (error) => {
        console.error("Onfido exception:", error);
        patchSDKToken({
          accountID: accountId,
          outcome: "SDK_ERROR",
          token: activeSdkToken,
        });
        captureOnfidoException(error, {
          error_type: error?.type,
        });

        setIsStarted(false);
        setActiveSdkToken(null);

        if (RESTART_ONFIDO_ERROR_TYPES.includes(error?.type)) {
          userConsentErrorModal.toggle();
          return;
        }

        setErrorMessage(error?.message ?? "An unexpected error occurred.");
        errorModal.toggle();
      },
      containerId: "onfido-mount",
      theme: ONFIDO_THEME,
    };

    AmplitudeProvider.dispatch(
      "onfido_started",
      JSON.stringify({ onfido_id: onfidoApplicantIdRef.current }),
    );

    eventTracker("Started Onfido SDK flow", {
      category: "Onboarding",
    });

    const handle = Onfido.init(onfidoOptions);

    return () => {
      handle.tearDown();
    };
  }, [accountId, activeSdkToken, isStarted]);

  const isSetupErrorVisible = !!setupErrorMessage && !isStarted && !isCompleted;
  const isLoading =
    !setupErrorMessage &&
    !isStarted &&
    !isCompleted &&
    (!isApplicantReady || isCreatingOnfido || isUpdatingOnfido || isStarting);

  return (
    <div className="h-full">
      {!isSetupErrorVisible && !isLoading && (
        <div id="onfido-mount" className="h-full" />
      )}
      <div className="mt-4 mb-8 flex h-full flex-col items-center justify-center gap-4">
        {isLoading && <Spinner />}
        {isSetupErrorVisible && (
          <div className="flex flex-col items-center gap-4 text-center">
            <Text>{setupErrorMessage}</Text>
            <Button onClick={handleRestartOnfido}>
              Retry Identity Verification
            </Button>
          </div>
        )}
        {isCompleted && <Text>Thank you! You will be redirected shortly.</Text>}
      </div>
      {userConsentErrorModal.component}
      {errorModal.component}
    </div>
  );
};

export default OnfidoPage;
